import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Building, Landmark, Plane, Trees, Hotel, MapPin, Scale, Sparkles,
    ArrowRight, CheckCircle2, Send, ShieldCheck, Network,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { getPageStrings } from "@/lib/pageStrings";
import LanguagePicker from "@/components/LanguagePicker";

const AGENCIES = [
    {
        slug: "investment-fiji",
        name: "Investment Fiji",
        icon: Sparkles,
        scope: "Foreign Investment Registration Certificates · Investor permit endorsements · sectoral incentive applications",
        we_handle: [
            "Foreign Investment Registration Certificate (FIRC) preparation & filing",
            "Investor Permit endorsements with Department of Immigration",
            "Sector incentive applications (tourism, agriculture, hospitality)",
            "Annual reporting & compliance follow-ups",
        ],
        pain: "First touch-point for every foreign investor — but documentation is unforgiving and rejections cost you 4–8 weeks.",
    },
    {
        slug: "frcs",
        name: "Fiji Revenue & Customs Service (FRCS)",
        icon: Scale,
        scope: "Tax registration · TIN · VAT · PAYE · corporate income tax · customs",
        we_handle: [
            "Tax Identification Number (TIN) registration for entity & directors",
            "VAT registration & monthly returns",
            "PAYE registration for staff payroll",
            "Annual corporate tax return filing",
            "Customs duty advisory for imported equipment",
        ],
        pain: "Multi-form, multi-window registration. Miss a step and your bank account opening stalls.",
    },
    {
        slug: "department-immigration",
        name: "Department of Immigration",
        icon: Plane,
        scope: "Investor visas · work permits · dependent visas · permanent residency",
        we_handle: [
            "Investor permit application & medical clearance",
            "Work permits for foreign directors and key staff",
            "Spouse and dependent visas",
            "Permit renewals & change-of-conditions filings",
            "Permanent residency advisory",
        ],
        pain: "Forms reject silently if any KYC document is one day expired. We pre-check before lodgement.",
    },
    {
        slug: "ministry-lands",
        name: "Ministry of Lands & Mineral Resources",
        icon: MapPin,
        scope: "Freehold conversions · State land leases · sub-divisions · titles",
        we_handle: [
            "Freehold land conveyancing coordination",
            "State land lease applications & renewals",
            "Sub-division & title deed registrations",
            "Foreign-buyer land use approvals",
        ],
        pain: "Title searches, native land board cross-checks, and council overlays must all align — we map the full chain.",
    },
    {
        slug: "rbf",
        name: "Reserve Bank of Fiji",
        icon: Landmark,
        scope: "Capital inflow · outflow approvals · forex licences · banking supervision",
        we_handle: [
            "Capital inflow approval — getting your investment USD into Fiji",
            "Capital outflow approval — repatriating profits & exits",
            "Foreign exchange transaction advisory",
            "Banking & treasury structure approvals",
        ],
        pain: "Without RBF approval, your bank account is functionally frozen for foreign-source funds.",
    },
    {
        slug: "tltb",
        name: "iTaukei Land Trust Board (TLTB)",
        icon: Trees,
        scope: "Native land leases · Mataqali consents · lease renegotiations",
        we_handle: [
            "Native land lease applications (commercial, agricultural, residential)",
            "Mataqali consent coordination",
            "Lease renewal & rent renegotiation",
            "Goodwill payment structuring",
            "Landowner unit advisory",
        ],
        pain: "83% of Fiji's land is iTaukei. Skipping TLTB is impossible. Doing it without local relationships is brutal.",
    },
    {
        slug: "ministry-tourism",
        name: "Ministry of Tourism, Civil Aviation & Industry",
        icon: Hotel,
        scope: "Tourism Hotel Aid Act · resort licences · industry endorsements",
        we_handle: [
            "Tourism Hotel Aid Act incentive applications",
            "Short-Life Investment Package (SLIP) submissions",
            "Resort, hotel, and lodge operating licences",
            "Tourism industry classification & endorsements",
        ],
        pain: "Tourism's incentives are the most generous in the South Pacific — but only if your filings are timed and structured correctly.",
    },
    {
        slug: "department-environment",
        name: "Department of Environment",
        icon: ShieldCheck,
        scope: "Environment Impact Assessments · waste discharge consents · coastal permits",
        we_handle: [
            "Environment Impact Assessment (EIA) coordination",
            "Coastal & marine development consents",
            "Waste discharge & water-use permits",
            "Endangered species & protected area advisory",
        ],
        pain: "Almost every coastal resort or eco-development needs an EIA. Without one, your council application dies.",
    },
    {
        slug: "town-councils",
        name: "Town & City Councils (Suva · Nadi · Lautoka · Sigatoka)",
        icon: Building,
        scope: "Planning permits · building approvals · zoning · business licences",
        we_handle: [
            "Development & planning permit applications",
            "Building approval & inspection scheduling",
            "Zoning advisory & rezoning applications",
            "Annual business licence renewals",
        ],
        pain: "Every council has different rules. We hold direct relationships at all four major councils.",
    },
];

export default function GovernmentServices() {
    const [requestAgency, setRequestAgency] = useState(null);
    const { lang } = useLang();
    const t = getPageStrings("government", lang);

    return (
        <div data-testid="government-services-page" className="bg-background min-h-screen">
            {/* HERO */}
            <section className="relative overflow-hidden bg-ocean-deep-grad text-white py-20 lg:py-28">
                <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-coral/10 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-turquoise/10 blur-3xl pointer-events-none" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-coral/15 border border-coral/30 mb-6">
                        <Network className="h-3.5 w-3.5 text-coral" />
                        <span className="text-[10px] uppercase tracking-[0.25em] font-app font-bold text-coral">
                            {t.heroKicker}
                        </span>
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-5 max-w-4xl">
                        {t.heroTitlePre}<span className="italic text-turquoise">{t.heroTitleAccent}</span>{t.heroTitlePost}
                    </h1>
                    <p className="font-app text-base sm:text-lg text-white/85 leading-relaxed max-w-3xl mb-6">
                        {t.heroSubtitle}
                    </p>
                    <LanguagePicker variant="dark" testId="government-lang-select" />
                    <div className="grid sm:grid-cols-3 gap-4 mt-10">
                        {[
                            { stat: "9", label: t.statsAgencies },
                            { stat: "1", label: t.statsContact },
                            { stat: "0", label: t.statsForms },
                        ].map((m) => (
                            <div key={m.label} className="bg-white/5 border border-white/10 rounded-lg p-4">
                                <div className="font-display text-3xl font-bold text-turquoise tabular-nums">{m.stat}</div>
                                <div className="text-xs uppercase tracking-wider text-white/70 font-app mt-1">{m.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AGENCIES GRID */}
            <section className="py-16 lg:py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-10">
                        <h2 className="font-display text-3xl lg:text-4xl font-bold mb-3 text-foreground">
                            {t.bodyTitle}
                        </h2>
                        <p className="font-app text-base text-muted-foreground max-w-2xl">
                            {t.bodyIntro}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                        {AGENCIES.map((a, i) => {
                            const Icon = a.icon;
                            return (
                                <Card
                                    key={a.slug}
                                    data-testid={`agency-card-${a.slug}`}
                                    className="p-6 hover:border-coral/50 transition-all hover:shadow-lg bg-card group"
                                >
                                    <div className="flex items-start gap-3 mb-4">
                                        <div className="h-12 w-12 rounded-lg bg-ocean/10 grid place-items-center group-hover:bg-coral/15 transition shrink-0">
                                            <Icon className="h-5 w-5 text-ocean dark:text-turquoise group-hover:text-coral transition" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app font-bold mb-1">
                                                {t.agencyLabelPrefix} {String(i + 1).padStart(2, "0")}
                                            </div>
                                            <h3 className="font-display text-lg font-bold leading-tight text-foreground">
                                                {a.name}
                                            </h3>
                                        </div>
                                    </div>

                                    <p className="font-app text-xs uppercase tracking-wider text-muted-foreground mb-2 font-bold">
                                        {t.scopeLabel}
                                    </p>
                                    <p className="font-app text-sm text-foreground/80 mb-4 leading-relaxed">
                                        {a.scope}
                                    </p>

                                    <div className="bg-coral/5 dark:bg-coral/10 border border-coral/20 rounded-lg p-3 mb-4">
                                        <p className="font-app text-[11px] uppercase tracking-wider text-coral font-bold mb-1">
                                            {t.painLabel}
                                        </p>
                                        <p className="font-app text-sm text-foreground/80 italic leading-relaxed">
                                            {a.pain}
                                        </p>
                                    </div>

                                    <p className="font-app text-xs uppercase tracking-wider text-fiji-green mb-2 font-bold">
                                        {t.handleLabel}
                                    </p>
                                    <ul className="space-y-1.5 mb-5">
                                        {a.we_handle.map((s, j) => (
                                            <li key={j} className="flex items-start gap-2 text-sm font-app text-foreground/85">
                                                <CheckCircle2 className="h-4 w-4 text-fiji-green mt-0.5 shrink-0" />
                                                <span>{s}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <Button
                                        size="sm"
                                        data-testid={`agency-request-btn-${a.slug}`}
                                        onClick={() => setRequestAgency(a)}
                                        className="w-full bg-ocean-green hover:bg-ocean text-white font-app group-hover:bg-coral group-hover:hover:bg-coral-deep transition"
                                    >
                                        {t.handleCta}
                                        <ArrowRight className="h-3.5 w-3.5 ml-1.5 group-hover:translate-x-0.5 transition" />
                                    </Button>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA STRIP */}
            <section className="py-14 bg-coral/5 dark:bg-coral/10 border-y border-coral/20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="font-display text-2xl lg:text-3xl font-bold mb-3 text-foreground">
                        {t.bottomCtaTitle}
                    </h2>
                    <p className="font-app text-muted-foreground mb-5 max-w-2xl mx-auto">
                        {t.bottomCtaBody}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button asChild size="lg" data-testid="govt-cta-concierge" className="bg-coral hover:bg-coral-deep text-white">
                            <Link to="/concierge">{t.bottomCtaPrimary} <ArrowRight className="h-4 w-4 ml-2" /></Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" data-testid="govt-cta-strategy">
                            <Link to="/invest">{t.bottomCtaSecondary}</Link>
                        </Button>
                    </div>
                </div>
            </section>

            {requestAgency && (
                <AgencyRequestDialog
                    agency={requestAgency}
                    onClose={() => setRequestAgency(null)}
                />
            )}
        </div>
    );
}

function AgencyRequestDialog({ agency, onClose }) {
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "", country: "",
        message: `I'd like Bula Real Estate Fiji to handle my interactions with ${agency.name}.\n\nProject context:\n`,
        budget_band: "", timeline: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.message) {
            toast.error("Name, email, and message are required");
            return;
        }
        setSubmitting(true);
        try {
            // Reuse existing /services/request endpoint, slug: government-liaison + agency in message
            await api.post("/services/request", {
                ...form,
                service_slug: "government-liaison",
                message: `[Agency: ${agency.name}]\n\n${form.message}`,
            });
            setDone(true);
            toast.success("Request sent — we'll reply within one business day.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not send request");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="agency-request-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">Handle: {agency.name}</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        We'll reply within one business day with a scoped engagement and timeline.
                    </DialogDescription>
                </DialogHeader>

                {done ? (
                    <div className="py-8 text-center" data-testid="agency-request-done">
                        <CheckCircle2 className="h-12 w-12 text-fiji-green mx-auto mb-3" />
                        <div className="font-display text-xl font-bold text-foreground mb-2">Request received</div>
                        <p className="font-app text-sm text-muted-foreground">
                            Our team will reply to <strong className="text-foreground">{form.email}</strong> within one business day.
                        </p>
                        <Button onClick={onClose} className="mt-5 bg-ocean-green text-white">Close</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3" data-testid="agency-request-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Full name *</Label>
                                <Input data-testid="agency-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Email *</Label>
                                <Input data-testid="agency-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Phone (E.164)</Label>
                                <Input data-testid="agency-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+1..." className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Country</Label>
                                <Input data-testid="agency-country" value={form.country} onChange={(e) => update("country", e.target.value)} placeholder="USA / Australia / NZ…" className="bg-card text-foreground" />
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Investment band</Label>
                                <Select value={form.budget_band} onValueChange={(v) => update("budget_band", v)}>
                                    <SelectTrigger data-testid="agency-budget" className="bg-card text-foreground"><SelectValue placeholder="Select…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="under-250k">Under USD 250k</SelectItem>
                                        <SelectItem value="250k-1m">USD 250k – 1m</SelectItem>
                                        <SelectItem value="1m-5m">USD 1m – 5m</SelectItem>
                                        <SelectItem value="5m-plus">USD 5m+</SelectItem>
                                        <SelectItem value="exploring">Just exploring</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-app text-muted-foreground">Timeline</Label>
                                <Select value={form.timeline} onValueChange={(v) => update("timeline", v)}>
                                    <SelectTrigger data-testid="agency-timeline" className="bg-card text-foreground"><SelectValue placeholder="Select…" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="now">Ready now</SelectItem>
                                        <SelectItem value="3-months">Within 3 months</SelectItem>
                                        <SelectItem value="6-months">Within 6 months</SelectItem>
                                        <SelectItem value="exploring">Exploring</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs font-app text-muted-foreground">Project context *</Label>
                            <Textarea data-testid="agency-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} required className="bg-card text-foreground resize-none" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={onClose} className="font-app">Cancel</Button>
                            <Button type="submit" disabled={submitting} data-testid="agency-submit-btn" className="bg-coral hover:bg-coral-deep text-white">
                                <Send className="h-4 w-4 mr-2" />
                                {submitting ? "Sending…" : "Send request"}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
